import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useSeo, SITE_URL } from "../lib/seo";
import { PublicHeader, PublicFooter } from "../components/PublicShell";
import { ShareButton } from "../components/ShareButton";
import { Input } from "../components/ui/input";
import { Search, ChevronRight, BookOpen } from "lucide-react";

export default function Guides() {
  const [guides, setGuides] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/public/guides").then(({ data }) => setGuides(data.guides)).catch(() => {}); }, []);

  useSeo({
    title: "Clinical Guides for Dentists — Medically Complex Patients | DentiCision",
    description: "Free, referenced dental management guides for 80+ medical conditions and medications: anticoagulants, antiplatelets, cardiac conditions, diabetes, pregnancy, MRONJ, transplants, oncology and more.",
    path: "/guides",
    jsonLd: guides.length ? {
      "@context": "https://schema.org", "@type": "CollectionPage", name: "DentiCision Clinical Guides", url: `${SITE_URL}/guides`,
      mainEntity: { "@type": "ItemList", itemListElement: guides.map((g, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}/guides/${g.slug}`, name: g.title })) },
    } : null,
  });

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    return guides.filter((g) => !s || g.name.toLowerCase().includes(s) || g.category.toLowerCase().includes(s))
      .reduce((a, g) => { (a[g.category] = a[g.category] || []).push(g); return a; }, {});
  }, [guides, q]);

  return (
    <div className="min-h-screen bg-white" data-testid="guides-page">
      <PublicHeader />
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#16B5A6]/10 text-[#0B7569] text-xs font-semibold"><BookOpen className="w-3.5 h-3.5" /> Free · Referenced · Updated</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2545] mt-4">Clinical Guides for Dentists</h1>
          <p className="text-base md:text-lg text-slate-600 mt-4 max-w-3xl">Evidence-based dental management of medically complex patients — what to do in an emergency, how to plan routine care, which tests to request and which drugs to avoid.</p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <div className="relative w-full sm:w-96">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="guides-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a condition or medication (e.g. warfarin)" className="pl-9 h-11 bg-white" />
            </div>
            <ShareButton path="/guides" text="Free referenced dental management guides for medically complex patients — DentiCision:" testid="guides-share-btn" />
          </div>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-10">
        {Object.entries(groups).map(([cat, items]) => (
          <section key={cat}>
            <h2 className="font-heading text-base md:text-lg font-semibold text-[#0B2545] border-b border-slate-200 pb-2">{cat}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              {items.map((g) => (
                <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`guide-link-${g.slug}`}
                  className="group flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:border-[#1C6FE3] hover:bg-sky-50/50 transition-colors">
                  <span className="text-sm font-medium text-slate-800">{g.name}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#1C6FE3] transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </section>
        ))}
        {guides.length > 0 && Object.keys(groups).length === 0 && <p className="text-slate-500">No guide found. <Link to="/register" className="text-[#1C6FE3]">Sign up</Link> and suggest it to our team.</p>}
      </main>
      <PublicFooter />
    </div>
  );
}
